import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AccountsService, AccountFeatures, Account, IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { Rule, RuleDefinition, RulesDataService } from '@services/rules-data.service';
import { combineLatest, distinctUntilChanged, filter, finalize, firstValueFrom, map, Subject, take, takeUntil } from 'rxjs';
import { convertEpochToDateString } from '@functions/functions'
import { CreateRuleComponent, EditRuleDialogData } from 'src/app/dialogs/create-rule/create-rule.component';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { MatSelect } from '@angular/material/select';
import { RuleListComponent, RuleListDialogData } from 'src/app/dialogs/rule-list/rule-list.component';

/** Used to filter the table of Rules */
export interface FilterRuleData {
	Enabled: boolean;
	Disabled: boolean;
	Active: boolean;
	CMMS: boolean;
}

@Component({
	selector: 'app-rules',
	templateUrl: './rules.component.html',
	styleUrls: ['./rules.component.scss']
})
export class RulesComponent implements OnInit {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** All Rules */
	public rules: Rule[] = [];

	/** All Assets */
	public assetList: LevelAsset[] = [];

	/** Table fields */
	public ruleFields: Object = { text: 'RuleDescription', id: 'RuleID' };

	/** Table fields */
	public assetFields: Object = { text: 'Name', id: 'AssetID' };

	/** filterLocationAssetIDs */
	filterLocationAssetIDs: number[] = [];

	/** locationHierarchyFilter */
	public locationHierarchyFilter?: OrganisationsHierarchy;

	/** Initialised filterRuleData */
	public filterRuleData: FilterRuleData = {
		Enabled: false, Active: false, Disabled: false, CMMS: false
	}

	public hasFilters: boolean = false;

	/** CMMSFeatureEnabled */
	public CMMSFeatureEnabled: boolean = false;

	/** accountID */
	@Input() accountGUID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	@Input() public OrganisationUID: string;

	/** displayedRuleColumns */
	displayedRuleColumns: string[] = [
		'RuleDescription',
		'condition',
		'AssetName',
		'lastTriggered',
		'eventsInPast30Days',
		'CMMSEnabled',
		'buttons',
		'GoToAsset'
	];

	/** displayedAssetColumns */
	displayedAssetColumns: string[] = [
		'Name',
		'Description',
		'HierarchyPath',
		'Buttons',
		'GoToAsset'
	];

	/** filteredRuleColumns */
	filteredRuleColumns: string[] = []

	/** filteredAssetColumns */
	filteredAssetColumns: string[] = this.displayedAssetColumns

	/** datasource */
	rulesDataSource: MatTableDataSource<Rule>;
	rulesReturned: boolean = false;
	/** datasource */
	assetsDataSource: MatTableDataSource<LevelAsset>;
	assetsReturned: boolean = false;

	/** Table paginator */
	@ViewChild("MatRulePaginator") MatRulePaginator: MatPaginator;
	/** Table sorting */
	@ViewChild("rulesSort") MatRuleSort: MatSort;

	/** Table paginator */
	@ViewChild("MatAssetPaginator") MatAssetPaginator: MatPaginator;
	/** Table sorting */
	@ViewChild("assetsSort") MatAssetSort: MatSort;

	/** locationdropdown */
	@ViewChild('locationdropdown') locationdropdown: MatSelect;

	/** List of Tab IDs */
	public tabIDList: string[] = ["Rule", "NoRule"]

	/** currentUser */
	public currentUser: WebAppUser;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/** hierarchyBreadcrumbs */
	private hierarchyBreadcrumbs: Map<number, string>;

	/** hierarchyAssetIDs */
	private hierarchyAssetIDs: Set<any>;

	/** MainLocations */
	public MainLocations: OrganisationsHierarchy[] = [];

	/** locationFields */
	public locationFields: { text: string, value: string } = { text: "Name", value: "Id" };

	/** selectedTab */
	public selectedTab: number = 0

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService
	 * @param assetsDataService assetsDataService
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public assetsDataService: AssetsDataService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		public accountsService: AccountsService,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) { }

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.globalFunctions.displaySpinner(true)

		this.setupRuleCallbacks();
		this.setupAssetCallbacks();

		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(),
			this.siteSubscriptions.selectedAccount$.pipe(),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(),
			this.siteSubscriptions.hierarchicalData$.pipe(),
			this.authenticationService.currentUser$.pipe(),
			this.route.queryParamMap.pipe(take(1))
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedAccount, selectedOrganisationUID, hierarchicalData, currentUser, queryParamMap]) =>
				!!selectedAccountGUID && !!selectedAccount && !!selectedOrganisationUID && !!hierarchicalData && !!currentUser && !!queryParamMap)
		).subscribe({
			next: async ([selectedAccountGUID, account, selectedOrganisationUID, hierarchicalData, currentUser, queryParamMap]) => {

				this.accountGUID = selectedAccountGUID;
				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.CMMSFeatureEnabled = AccountsService.checkFeature(AccountFeatures.SAP, this.selectedAccount);

				if (!this.CMMSFeatureEnabled) { this.filteredRuleColumns = this.displayedRuleColumns.filter(column => column !== 'CMMSEnabled'); }
				else { this.filteredRuleColumns = this.displayedRuleColumns; }

				this.OrganisationUID = selectedOrganisationUID;

				this.currentUser = currentUser;

				this.hierarchy = JSON.parse(hierarchicalData);
				this.hierarchyBreadcrumbs = this.flattenAssetBreadcrumbs(this.hierarchy);
				this.hierarchyAssetIDs = new Set(this.flattenAssets(this.hierarchy).map(a => a.AssetID));

				if (this.hierarchy[0].SubChild) {
					this.hierarchy[0].SubChild.forEach(subchild => {
						this.MainLocations.push(subchild);
					});
				}

				if (!this.currentUser.AdminSite && !this.selectedAccount.Admin) {
					this.filteredRuleColumns.pop(); //hide edit button for non admins
				}

				var savedTab = queryParamMap.get('selectedTab') ?? ""
				this.selectedTab = this.tabIDList.indexOf(savedTab)

				await Promise.all([
					this.getRules(),
					this.getAssets()
				]);

				this.globalFunctions.displaySpinner(false)
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
			// , complete: () => { this.globalFunctions.displaySpinner(false) }
		});
	}

	/**
	 * Get the Rules 
	 */
	async getRules() {

		try {
			const rules = await firstValueFrom(
				this.rulesDataService.getRules$(this.accountGUID, this.OrganisationUID, 0, 0).pipe(
					filter(result => !!result),
					map(result => result.filter((rule: Rule) => this.hierarchyAssetIDs.has(rule.AssetID)))
				)
			);

			if (rules) {
				this.rules = rules;
				this.rulesDataSource.data = this.rules;

				setTimeout(() => {
					this.setupRuleCallbacks()
				}, 10);

				if (this.rulesDataSource.sort) {
					this.rulesDataSource.sort.active = 'AssetName'; // Default column to sort by
					this.rulesDataSource.sort.direction = 'asc'; // Default sorting direction
				}

				this.rulesReturned = true;

			}
			else {
				this.globalFunctions.DisplaySnackBar({
					message: "Error Getting Rules",
					duration: 5000,
					action: ""
				});
			}
		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, "this.rulesDataService.getRules$");
			}
		} finally {

		}

	}

	/**
	 * get the Organisation Assets
	 */
	async getAssets() {

		try {

			const assets = await firstValueFrom(
				this.assetsDataService.getOrganisationAssets$(this.accountGUID, this.OrganisationUID)
					.pipe(
						filter((result) => !!result)
						// , map(result =>
						// 	result
						// 		.filter((x: LevelAsset) => x.RuleCount === 0)
						// 		.map((x: LevelAsset) => {
						// 			x.HierarchyPath = this.hierarchyBreadcrumbs.get(x.AssetID) ?? '';
						// 			return x;
						// 		})
						// )
					)
			);

			if (assets) {
				this.assetList = (<LevelAsset[]>assets)
				this.assetsDataSource.data = this.assetList.filter((x: LevelAsset) => x.RuleCount === 0)
					.map((x: LevelAsset) => {
						x.HierarchyPath = this.hierarchyBreadcrumbs.get(x.AssetID) ?? '';
						return x;
					});;

				setTimeout(() => {
					this.setupAssetCallbacks();
				}, 10);


				if (this.assetsDataSource.sort) {
					this.assetsDataSource.sort.active = 'Name'; // Default column to sort by
					this.assetsDataSource.sort.direction = 'asc'; // Default sorting direction
				}

				this.assetsReturned = true;
			}
			else {
				this.globalFunctions.DisplaySnackBar({
					message: "Error Getting Asset List",
					duration: 5000,
					action: ""
				});
			}
		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, "this.assetsDataService.getOrganisationAssets$");
			}
		} finally {

		}
	}

	/** GoToRule */
	GoToRule(rule: Rule) {
		this.router.navigate([`./${this.accountGUID}/organisation/${this.OrganisationUID}/rules/${rule.RuleID}`])
	}

	/** Filters rule table based on filterData */
	filterRules() {
		// Force MatTableDataSource to re-run filterPredicate
		const currentFilter = this.rulesDataSource.filter;
		this.rulesDataSource.filter = '';
		this.rulesDataSource.filter = currentFilter + ' ' || ' ';
	}

	/** filterActiveRules */
	filterActiveRules(event: any) {
		this.filterRuleData.Active = event.checked
		this.filterRules();
	}

	/** filterEnabledRules */
	filterEnabledRules(event: any) {
		this.filterRuleData.Enabled = event.checked
		this.filterRules();
	}

	/** filterDisabledRules */
	filterDisabledRules(event: any) {
		this.filterRuleData.Disabled = event.checked
		this.filterRules();
	}

	filterCMMSRules(event: any) {
		this.filterRuleData.CMMS = event.checked
		this.filterRules();
	}


	/**
	 * filterRulesByLocation
	 * @param event event 
	 * @param cancel cancel 
	 */
	filterByLocation(event: any, cancel?: boolean) {
		this.filterLocationAssetIDs = [];
		if (cancel) {
			this.locationHierarchyFilter = undefined;
			this.locationdropdown.value = null;
		} else if (event.value) {
			this.locationHierarchyFilter = <OrganisationsHierarchy>event.value;
			this.flattenAssets([this.locationHierarchyFilter]).forEach(asset => {
				this.filterLocationAssetIDs.push(asset.AssetID);
			});
		}

		this.filterRules();
		this.filterAssets();
	}


	/** Filters rule table based on filterData */
	filterAssets() {
		// Force MatTableDataSource to re-run filterPredicate
		const currentFilter = this.assetsDataSource.filter;
		this.assetsDataSource.filter = '';
		this.assetsDataSource.filter = currentFilter + ' ' || ' ';
	}

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes
	 * @returns
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr
	 * @returns
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}

	/** applyRulesFilter */
	applyRulesFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.rulesDataSource.filter = filterValue.trim().toLowerCase();

		if (this.rulesDataSource.paginator) {
			this.rulesDataSource.paginator.firstPage();
		}
	}

	/** applyAssetFilter */
	applyAssetFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.assetsDataSource.filter = filterValue.trim().toLowerCase();

		if (this.assetsDataSource.paginator) {
			this.assetsDataSource.paginator.firstPage();
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	getAssetForRule(rule: Rule, event: MouseEvent) {
		event.stopPropagation();
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getOrganisationAssets$(this.accountGUID, this.OrganisationUID, rule.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((ruleEvents) => !!ruleEvents),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (asset: any) => {
				this.onEditRule(rule, asset);
			},
			error: (error) => {
				if (error.status == 404) { }
				else { this.globalFunctions.HandleError(error, "Error: AssetsDataService.getOrganisationAssets$", false); }
			},
		});
	}

	/**
	 * Edit an existing rule
	 * @param rule
	 * @param event
	 */
	onEditRule(rule: Rule, asset: LevelAsset) {

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...rule };

		let time: string = this.convertMinutesToHHMM(_edit.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(_edit.TriggerTime)[1]

		let dialogData = <EditRuleDialogData>{
			assetItem: asset,
			ruleItem: _edit,
			triggerTime: time,
			comparison: _edit.RuleText.match(/AND|OR/)?.[0] || ""
		}

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(async result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			if (result.ruleEnabled == null) {
				rule.RuleEnabled = false;
			} else {
				rule.RuleEnabled = result.ruleEnabled
			}

			if (result.CMMSEnabled == null) {
				rule.CMMSEnabled = false;
			} else {
				rule.CMMSEnabled = result.CMMSEnabled
			}

			rule.RuleDescription = result.ruleDescription
			rule.RuleMessage = result.ruleMessage
			rule.TriggerTime = this.timeToMinutes(result.ruleTime)

			//split the rule into the rule-definition composition

			if (rule.RuleDef) {
				let data1;
				if (result.condition1 != '~' && result.condition1 != '!~') {
					data1 = {
						RuleDefID: rule.RuleDef[0].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 1,
						DatapointID: result.dataPoint1,
						Limit: result.value1,
						Operator: result.condition1
					}
				} else {
					data1 = {
						RuleDefID: rule.RuleDef[0].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 1,
						DatapointID: result.dataPoint1,
						Limit: result.between1value1,
						LowerLimit: result.between1value2,
						Operator: result.condition1
					}
				}

				let definition1: RuleDefinition = data1;

				let definition2 = undefined;
				/** if we already had another definition */
				if (result.dataPoint2 && rule.RuleDef[1]) {
					let data2;
					if (result.condition2 != '~' && result.condition2 != '!~') {
						data2 = {
							RuleDefID: rule.RuleDef[1].RuleDefID,
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.value2,
							Operator: result.condition2
						}
					} else {
						data2 = {
							RuleDefID: rule.RuleDef[1].RuleDefID,
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.between2value1,
							LowerLimit: result.between2value2,
							Operator: result.condition2
						}
					}
					definition2 = data2;
					/** if a new definition is being added */
				} else if (result.dataPoint2 && !rule.RuleDef[1]) {
					let data2;
					if (result.condition2 != '~' && result.condition2 != '!~') {
						data2 = {
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.value2,
							Operator: result.condition2
						}
					} else {
						data2 = {
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.between2value1,
							LowerLimit: result.between2value2,
							Operator: result.condition2
						}
					}
					definition2 = data2;
				}

				if (definition2) {
					rule.RuleText = "[1] " + result.comparison + " [2]";
					rule.RuleDef = [definition1, definition2];

				} else {
					rule.RuleText = "[1]";
					rule.RuleDef = [definition1];
				}
			}

			await this.patchRule(rule);
		});
	}

	/**
	 * patchRule
	 * @param rule
	 */
	async patchRule(rule: Rule, deleted: boolean = false): Promise<Rule> {
		try {
			const response = await firstValueFrom(
				this.rulesDataService.patchRule$(this.accountGUID, this.OrganisationUID, rule.AssetID, rule, this.currentUser.UserID, deleted)
			);

			// console.log(sqlResult)
			this.globalFunctions.DisplaySnackBar({
				message: !deleted ? "Rule Updated" : "Rule Deleted",
				type: SnackType.Info,
				duration: 1000
			});

			return response[0];

		} catch (error: any) {
			if (error.status != 404) {
				console.error(error)
				this.globalFunctions.HandleError(error, "this.rulesDataService.patchRule$: Error retrieving Users")
			}
			return {} as Rule;
		}
		finally { }
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * Returns a map of AssetID -> Hierarchy breadcrumb
	 */
	flattenAssetBreadcrumbs(
		hierarchy: OrganisationsHierarchy[],
		path: string[] = [],
		result = new Map<number, string>()
	): Map<number, string> {

		if (!Array.isArray(hierarchy)) {
			return result;
		}

		for (const item of hierarchy) {

			const currentPath = [...path, item.Name];

			// Store breadcrumb for every asset at this level
			for (const asset of item.Assets ?? []) {
				result.set(asset.AssetID, currentPath.join(' > '));
			}

			// Recurse into children
			this.flattenAssetBreadcrumbs(
				item.SubChild,
				currentPath,
				result
			);
		}

		return result;
	}

	/**
	 * deleteRule
	 */
	deleteRule(rule: Rule) {
		this.globalFunctions.UserConfirm$(`Delete ${rule.RuleDescription}?`).subscribe(async OKResult => {
			if (OKResult) {
				let deletedRule = await this.patchRule(rule, true);
				this.rules = this.rules.filter((x: Rule) => x.RuleID != deletedRule.RuleID);
				this.filterRules();
			}
		});
	}

	/**
	 * tabChange
	 * @param event event  
	 */
	tabChange(event: any) {
		this.selectedTab = event.selectedIndex;
		let tabName = this.tabIDList[event.selectedIndex];
		switch (event.selectedIndex) {
			case 0:
				setTimeout(() => {
					this.setupRuleCallbacks()
				}, 10);

				break;
			case 1:
				setTimeout(() => {
					this.setupAssetCallbacks();
				}, 10);
				break;
		}
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { selectedTab: tabName },
			queryParamsHandling: 'merge'
		});
	}

	/**
	 * setupRuleCallbacks
	 */
	setupRuleCallbacks() {

		if (!this.rulesDataSource) {
			this.rules = [];
			this.rulesDataSource = new MatTableDataSource(this.rules);
		}

		this.rulesDataSource.paginator = this.MatRulePaginator;
		this.rulesDataSource.sort = this.MatRuleSort;

		this.rulesDataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'RuleDescription': return item.RuleDescription || '';
				case 'AssetName': return item.AssetName || '';
				case 'lastTriggered': return item.LastTriggered || '';
				case 'eventsInPast30Days': return item.EventsInPast30Days || '';
				default: return '';
			}
		};

		this.rulesDataSource.filterPredicate = (rule: Rule, filter: string) => {

			/* Remove any Leading or following Spaces */
			filter = filter.trim();

			if ((this.filterRuleData.Active && !rule.Active) ||
				(this.filterRuleData.Disabled && rule.RuleEnabled) ||
				(this.filterRuleData.Enabled && !rule.RuleEnabled) ||
				(this.filterRuleData.CMMS && !rule.CMMSEnabled)) {
				return false;
			}

			if (this.locationHierarchyFilter) {
				if (!this.filterLocationAssetIDs.includes(rule.AssetID)) {
					return false;
				}
			}

			if (!filter) { return true; }

			const textToSearch =
				`${rule.AssetName ?? ''}${rule.RuleDescription ?? ''}${rule.RuleMessage ?? ''}`.toLowerCase();

			return textToSearch.includes(filter);
		};

	}

	/**
	 * setupAssetCallbacks
	 */
	setupAssetCallbacks() {

		if (!this.assetsDataSource) {
			this.assetList = [];
			this.assetsDataSource = new MatTableDataSource(this.assetList);
		}

		this.assetsDataSource.paginator = this.MatAssetPaginator;
		this.assetsDataSource.sort = this.MatAssetSort;

		this.assetsDataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'Name': return item.Name || '';
				case 'Description': return item.Description || '';
				default: return '';
			}
		};

		this.assetsDataSource.filterPredicate = (asset: LevelAsset, filter: string) => {

			/* Remove any Leading or following Spaces */
			filter = filter.trim();

			if (this.locationHierarchyFilter) {
				if (!this.filterLocationAssetIDs.includes(asset.AssetID)) {
					return false;
				}
			}

			if (!filter) { return true; }

			const textToSearch =
				`${asset.Name ?? ''}${asset.Description ?? ''}`.toLowerCase();

			return textToSearch.includes(filter);
		};

	}

	/**
	 * getFilterMessage
	 * @returns 
	 */
	getFilterMessage(): string {
		this.hasFilters = false;
		let showingResult: string = "Showing ";
		let amount: number = 0;
		let hasLocation: boolean = false;
		Object.entries(this.filterRuleData).forEach(([key, value]) => {
			if (value) {
				this.hasFilters = true;
				if (key != "Location") {
					if (amount == 0) {
						showingResult += " " + key
					} else {
						showingResult += ", " + key
					}
					amount++;
				}
			}
		});
		if (this.locationHierarchyFilter) {
			hasLocation = true;
		}
		if (amount == 0) {
			return showingResult + (hasLocation ? `all rules in ${this.locationHierarchyFilter?.Name}` : "all rules");
		}
		return showingResult + (hasLocation ? " rules in " + this.locationHierarchyFilter?.Name : " rules");
	}

	/**
	 * getLocationMessage
	 * @returns 
	 */
	getLocationMessage(): string {
		let result: string = "Showing ";
		let hasLocation: boolean = false;
		if (this.locationHierarchyFilter) {
			hasLocation = true;
		}
		return result + (hasLocation ? "Assets in " + this.locationHierarchyFilter?.Name : "All Assets");
	}

	/**
	 * ManageRules
	 * Open Rule List as a pop up dialog and manage rules for the selected asset based on the result
	 */
	ManageRules(selectedAsset: LevelAsset) {
		let data: RuleListDialogData = {
			asset: selectedAsset,
			isAdmin: this.currentUser.AdminSite || this.selectedAccount.Admin,
			currentUser: this.currentUser,
			accountGUID: this.accountGUID,
			organisationUID: this.OrganisationUID,
		}

		const dialogRef = this.dialog.open(RuleListComponent, {
			data: data
		});

		dialogRef.afterClosed().pipe(
			// filter(result => !!result)
		).subscribe(result => {
			this.getRules();
		});
	}

	/**
	 * findAsset
	 * @param assetID assetID  
	 * @returns 
	 */
	getAsset(assetID: number): LevelAsset | undefined {
		return this.assetList.find((x: LevelAsset) => x.AssetID == assetID);
	}

	/**
	 * goToAsset
	 * @param row 
	 */
	goToAsset(row: any) {
		let asset = this.getAsset(row.AssetID);
		if (asset) {
			this.siteSubscriptions.SetSelectedAsset(asset);
			this.router.navigate([`./${this.accountGUID}/organisation/${this.OrganisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
		}
	}

}
